import { buildOnlineServiceAnalyticsEventFile } from './onlineServiceAnalyticsResourceBuilder';
import { fireViewItemList, fireSelectItem, fireViewSearchResults, getPageBreadcrumbCategoryPath } from './ga4-events';
import { GetSearchResults } from './search-utils';
import { getRecommenderName } from './recommender-context';

/**
 * Engine-total result count for view_search_results on SS-takeover pages.
 * GetSearchResults() already prioritizes SS cache total → LG_PAGE_DATA
 * productResultsTotal → "of N" header text; the DOM card count is a
 * per-page rendered subset, never the total (TIG audit F-07) — last resort only.
 * @returns {number}
 */
export function resolveSearchResultsCount() {
    const engineTotal = GetSearchResults();
    return engineTotal > 0 ? engineTotal : document.querySelectorAll('article.card').length;
}

/**
 * Whether SearchSpring has rendered a result COUNT, read from `doc` (no window),
 * so it is unit-testable. The only cross-store signal is `.category__title-count`
 * showing "of N" — StoreSupply sets a `ss__loaded` body class, LoveGroomers does
 * not, so we key off the rendered count text, never SS classes. Zero-result
 * searches render no count element and so never become ready (skipped by design,
 * same as a render failure).
 *
 * @param {Document} doc
 * @returns {{ready: boolean, count: number}}
 */
export function searchResultsRenderState(doc) {
    const el = doc && typeof doc.querySelector === 'function' ? doc.querySelector('.category__title-count') : null;
    const m = el && el.textContent ? el.textContent.match(/of\s+([\d,]+)/i) : null;
    if (m) return { ready: true, count: parseInt(m[1].replace(/,/g, ''), 10) || 0 };
    return { ready: false, count: 0 };
}

// Module-level guard: the readiness watcher is armed at most once, no matter how
// many times initListingClickTracking() re-runs (onReady, listingPagesLoaded, grid
// MutationObserver re-fires).
let _vsrArmed = false;

/** Test-only: reset the arm-once guard between cases. */
export function __resetVsrWatcher() {
    _vsrArmed = false;
}

const VSR_READY_TIMEOUT_MS = 5000;

/**
 * Call `onReady()` once SearchSpring has rendered a result count, observing DOM
 * content (not SS body classes, which differ per store). Fires at most once. If
 * the count never renders within VSR_READY_TIMEOUT_MS (SS failure, or a
 * zero-result search), disconnects and does NOT fire.
 *
 * @param {() => void} onReady
 */
export function whenSearchResultsReady(onReady) {
    if (searchResultsRenderState(document).ready) {
        onReady();
        return;
    }
    let done = false;
    const finish = (ready) => {
        if (done) return;
        done = true;
        clearTimeout(timeout);
        observer.disconnect();
        if (ready) onReady();
    };
    const observer = new MutationObserver(() => {
        if (searchResultsRenderState(document).ready) finish(true);
    });
    const timeout = setTimeout(() => finish(false), VSR_READY_TIMEOUT_MS);
    observer.observe(document.body, { childList: true, subtree: true });
}

/**
 * Fire GA4 view_search_results for the current SS-takeover search page.
 *
 * When SearchSpring is enabled the result count comes from the SS-rendered
 * .category__title-count ("of N"), which isn't painted at onReady. Firing then
 * would read a stale/zero total and — because fireViewSearchResults session-dedupes
 * — lock it in before SS renders. SS's own ss-on-results-load event is unreliable
 * for this (StoreSupply never dispatches it on load; LoveGroomers dispatches it
 * before the count paints), so instead we wait for the count to actually render
 * (whenSearchResultsReady) and fire then. When SS is disabled we fire at onReady.
 * Term and count are read at fire time, so SPA navigations are reflected and
 * stale-state fires are URL-gated out downstream.
 */
export function scheduleViewSearchResults() {
    const fire = () => {
        const params = new URLSearchParams(window.location.search);
        const searchTerm = params.get('search_query') || params.get('fallbackQuery') || '';
        fireViewSearchResults(searchTerm, resolveSearchResultsCount());
    };

    const ssEnabled = String(window.BC_CONTEXT?.enableSearchSpring).toLowerCase() === 'true';
    if (!ssEnabled) {
        fire();
        return;
    }
    if (!_vsrArmed) {
        _vsrArmed = true;
        whenSearchResultsReady(fire);
    }
}

export function initListingClickTracking() {
    // Utility to grab SKU if available
    if (typeof document === 'undefined') return;
    function getSku(el) {
        let article = el.closest("article[data-original-sku-text]");
        if (article == null) {
            article = el.closest("article.card");
            if (article != null) {
                const star_container = article.querySelector('a.star_container');
                return star_container ? star_container.classList[1] : null;
            }

        }
        else {
            const skuDisplay = article ? article.querySelector("article > div.card-section.card-section--body.ss__result__details > div.card-brandwrap > p")?.textContent : null;
            if (skuDisplay) {
                return skuDisplay ? skuDisplay.replace(/^SKU:\s*/i, "").trim() : null;
            }
            else {
                return article.querySelector("article > div.card-section.card-section--body > div.card-brandwrap > p")?.textContent.replace(/^SKU:\s*/i, "").trim() || null;
            }
        }
    }
    function getAddToCartQuantity(el) {
        let qtyDataElement = el.closest("form").querySelector("form > input.form-input.form-input-qty");
        if (qtyDataElement) {
            let qty = parseInt(qtyDataElement.value);
            return isNaN(qty) || qty < 1 ? 1 : qty;
        }
        else {
            qtyDataElement = el.closest("article").querySelector("form > input.form-input.form-input-qty");
            if (qtyDataElement) {
                let qty = parseInt(qtyDataElement.value);
                return isNaN(qty) || qty < 1 ? 1 : qty;
            }
        }
    }
    // Track clicks
    function trackClick(type, el, recommender) {
        const sku = getSku(el);
        let article = el.closest("article");
        const price = article?.querySelector("span.price-primary")?.textContent ? parseFloat(article.querySelector("span.price-primary").textContent.replace(/[^0-9.-]/g, '')) : 0;
        let cardPosition = null;
        if (article != null && recommender === 'recommender') {
            const allArticles = Array.from(document.querySelectorAll("article[data-original-sku-text]"));
            cardPosition = allArticles.indexOf(article); // 1-based index
        }
        else {
            const allCards = Array.from(document.querySelectorAll("article.card"));
            cardPosition = allCards.indexOf(el.closest("article.card")); // 1-based index
        }
        //console.log("Track event:", { type, sku, href: el.href || null });
        const cartId = getCartId(); // function on window
        let recommenderName = null;
        if (recommender === 'recommender') {
            recommenderName = getRecommenderName(el);
            if (recommenderName == null) {
                console.debug("Recommender name not found for element:", el);
            }
        }

        let newEvent = {
            loadNumber: 1,
            scriptLoaded: null,
            interactionsCompleted: null,
            errorMessage: {
                eventName: type,
                eventMessage: "",
                timeStamp: new Date().toISOString()
            }
        };
        let quantity = null;
        let pageType = getPageType(); // function on window
        if (type == 'add_to_cart_start') {
            quantity = getAddToCartQuantity(el) || 1;
        }
        let eventObject = {
            tealium_event: type,
            sku: sku,
            href: el.href || null,
            page_type: pageType || "unknown",
            recommender: recommender,
            recommenderName: recommenderName,
            quantity: quantity
        };

        buildOnlineServiceAnalyticsEventFile(newEvent, eventObject, cartId);

        // GA4 select_item for name/image clicks that navigate to a PDP
        if (type === 'name_click' || type === 'image_click') {
            const article = el.closest('article.card') || el.closest('article');
            const name = article?.querySelector('.card-title a, .card-title')?.textContent?.trim() || '';
            const allCards = document.querySelectorAll('article.card');
            const idx = article ? [...allCards].indexOf(article) : 0;
            const listId = window.location.pathname.replace(/^\/|\/$/g, '').replace(/\//g, '_') || 'listing';

            const bcPath = getPageBreadcrumbCategoryPath();
            const listName = recommenderName
                || bcPath
                || document.querySelector('h1.page-heading, h1')?.textContent?.trim()
                || listId;

            const cardPayload = {
                sku,
                name,
                price,
                // Suppress breadcrumb categories on recommender clicks: the host PDP's
                // breadcrumb does not describe a recommender's item.
                categoryPath: recommenderName ? null : bcPath,
            };

            fireSelectItem(
                cardPayload,
                listId,
                listName,
                idx < 0 ? (cardPosition < 0 ? 0 : cardPosition) : idx,
            );
        }

        // Example: send to GTM dataLayer
        // window.dataLayer = window.dataLayer || [];
        // window.dataLayer.push({
        //   event: "product_click",
        //   action: type,
        //   sku,
        //   href: el.href || null
        // });
    }

    function addSafeEventListener(selector, event, handler) {
        document.querySelectorAll(selector).forEach(el => {
            // Prevent duplicate listeners by using a data attribute
            const key = `listener-${event}`;
            if (!el[key]) {
                el.addEventListener(event, handler);
                el[key] = true;
            }
        });
    }

    // --- 1. Add to Cart buttons ---
    // document.querySelectorAll("form.form-cardAdd [type=submit][ss-track-cart-add]")
    //     .forEach(btn => {
    //         btn.addEventListener("click", (e) => {
    //             trackClick("add_to_cart", e.currentTarget,"listingPage");
    //         });
    //     });
    //document.querySelectorAll("article.card > div.card-section-rightWrapper > div.card-section-rightWrapper--inner > div.card-section--buttons > form.form-cardAdd > input.button--cardAdd");
    addSafeEventListener("#ss__content--search form.form-cardAdd [type=submit][ss-track-cart-add]", "click", (e) => {
        trackClick("add_to_cart_start", e.currentTarget);
    });
    addSafeEventListener("#product-listing-container form.form-cardAdd [type=submit][ss-track-cart-add]", "click", (e) => {
        trackClick("add_to_cart_start", e.currentTarget);
    });
    // LG version of add to cart in listing
    addSafeEventListener("#searchspring-content form.form-cardAdd [type=submit][ss-track-cart-add]", "click", (e) => {
        trackClick("add_to_cart_start", e.currentTarget);
    });


    // --- 2. Swatch clicks ---
    // document.querySelectorAll(".card-swatch-item").forEach(link => {
    //     link.addEventListener("click", (e) => {
    //         trackClick("swatch_click", e.currentTarget,"listingPage");
    //     });
    // });
    addSafeEventListener("#ss__content--search > div.ss__has-results > ul > li > article > div.card-section.card-section--body.ss__result__details > div.card-swatches-container > a.card-swatch-item", "click", (e) => {
        trackClick("swatch_click", e.currentTarget);
    });
    // --- 2a. Swatch after search update
    // document.querySelectorAll(".ss__variants__column").forEach(link => {
    //     link.addEventListener("click", (e) => {
    //         trackClick("swatch_click", e.currentTarget);
    //     });
    // });
    addSafeEventListener(".ss__variants__column", "click", (e) => { // checked only works on one
        trackClick("swatch_click", e.currentTarget);
    });
    // --- 3. Product image clicks ---
    // document.querySelectorAll(".card-figure__link").forEach(link => {
    //     link.addEventListener("click", (e) => {
    //         trackClick("image_click", e.currentTarget);
    //     });
    // });
    addSafeEventListener(".card-figure__link", "click", (e) => {
        trackClick("image_click", e.currentTarget);
    });
    // LG version of image click in 
    addSafeEventListener("#searchspring-content > div > div.productCategoryCompare > div > ul > li > article > div > figure > a", "click", (e) => {
        trackClick("image_click", e.currentTarget);
    });

    // --- 4. Product name/title clicks ---
    // document.querySelectorAll(".card-title a").forEach(link => {
    //     link.addEventListener("click", (e) => {
    //         trackClick("name_click", e.currentTarget);
    //     });
    // });
    addSafeEventListener("#ss__content--search > div.ss__has-results > ul > li > article > div.card-section.card-section--body.ss__result__details > p.card-title a", "click", (e) => {
        trackClick("name_click", e.currentTarget);
    });
    // LG version of Name / Title click in 
    addSafeEventListener("#searchspring-content > div > div.productCategoryCompare > div > ul > li > article > div > p.card-title", "click", (e) => {
        trackClick("name_click", e.currentTarget);
    });
    // LG version of Choose options in listing
    // Choose options version 
    addSafeEventListener("#searchspring-content > div > div.productCategoryCompare > div > ul > li > article > div > div > div > a", "click", (e) => {
        trackClick("name_click", e.currentTarget);
    });

    // --- 4. Product reviews/stars clicks ---
    // document.querySelectorAll("article > div.card-section.card-section--body.ss__result__details > a").forEach(link => {
    //     link.addEventListener("click", (e) => {
    //         trackClick("star_review_click", e.currentTarget);
    //     });
    // });
    addSafeEventListener(".ruk_rating_snippet", "click", (e) => {
        trackClick("star_review_click", e.currentTarget);
    });
    addSafeEventListener("#ss__content--search > div.ss__has-results > ul > li > article > div.card-section.card-section--body.ss__result__details > a.star_container", "click", (e) => {
        trackClick("star_review_click", e.currentTarget);
    });
    // LG Product reviews/stars clicks ---
    addSafeEventListener("#searchspring-content > div > div.productCategoryCompare > div > ul > li > article > div a.star_container", "click", (e) => {
        trackClick("star_review_click", e.currentTarget);
    });
    
    addSafeEventListener(".productCarousel-slide > article > div.card-section.card-section--body > a.star_container", "click", (e) => {
        trackClick("star_review_click", e.currentTarget, "recommender");
    });
    addSafeEventListener("div.swiper-slide > div > article > article > div.card-section.card-section--body.ss__result__details > a.star_container", "click", (e) => {
        trackClick("star_review_click", e.currentTarget, "recommender");
    });
    // --- 5. Recommender Image Clicks
    // document.querySelectorAll(".productCarousel-slide > article > div.card-section-lefttWrapper > figure > a > div.card-img-container > img")
    // .forEach(link => {
    //     link.addEventListener("click", (e) => {
    //         trackClick("image_click", e.currentTarget,"recommender");
    //     });
    // });
    addSafeEventListener(".productCarousel-slide > article > div.card-section-lefttWrapper > figure > a > div.card-img-container > img", "click", (e) => {
        trackClick("image_click", e.currentTarget, "recommender");
    });
    addSafeEventListener("div.swiper-slide > div > article > article > div.card-section-lefttWrapper > figure > a > div > img", "click", (e) => {
        trackClick("image_click", e.currentTarget, "recommender");
    });
    // --- 6. Recommender Name/Title clicks ---
    // document.querySelectorAll(".productCarousel-slide > article > div.card-section.card-section--body > p > a")
    // .forEach(link => {
    //     link.addEventListener("click", (e) => {
    //         trackClick("name_click", e.currentTarget,"recommender");
    //     });
    // });
    addSafeEventListener(".productCarousel-slide > article > div.card-section.card-section--body > p > a", "click", (e) => {
        trackClick("name_click", e.currentTarget, "recommender");
    });
    addSafeEventListener("div.swiper-slide > div > article > article > div.card-section.card-section--body.ss__result__details > p > a", "click", (e) => {
        trackClick("name_click", e.currentTarget, "recommender");
    });
    // --- 7. Recommender Swatch clicks ---
    // document.querySelectorAll(".productCarousel-slide > article > div.card-section.card-section--body > div.card-swatches-container > a")
    // .forEach(link => {
    //     link.addEventListener("click", (e) => {
    //         trackClick("swatch_click", e.currentTarget,"recommender");
    //     });
    // });
    addSafeEventListener(".productCarousel-slide > article > div.card-section.card-section--body > div.card-swatches-container > a", "click", (e) => {
        trackClick("swatch_click", e.currentTarget, "recommender");
    });
    addSafeEventListener("div.swiper-slide > div > article > article > div.card-section.card-section--body.ss__result__details > div.card-swatches-container > a > span", "click", (e) => {
        trackClick("swatch_click", e.currentTarget, "recommender");
    });
    // --- 8. Recommender Add to Cart buttons ---
    // document.querySelectorAll(".productCarousel-slide > article > div.card-section-rightWrapper > div.card-section-rightWrapper--inner > div.card-section--buttons > form > input.button.button--primary.button--cardAdd")
    // .forEach(btn => {
    //         btn.addEventListener("click", (e) => {
    //             trackClick("add_to_cart", e.currentTarget,"recommender");
    //         });
    //     });
    addSafeEventListener(".productCarousel-slide > article > div.card-section-rightWrapper > div.card-section-rightWrapper--inner > div.card-section--buttons > form > input.button.button--primary.button--cardAdd", "click", (e) => {
        trackClick("add_to_cart_start", e.currentTarget, "recommender");
    });

    addSafeEventListener("div.swiper-slide > div > article > article > div.card-section-rightWrapper > div.card-section-rightWrapper--inner > div.card-section--buttons > form > input.button.button--primary.button--cardAdd", "click", (e) => {
        trackClick("add_to_cart_start", e.currentTarget, "recommender");
    });

    // --- 9.
    // document.querySelectorAll(".subcategoriesGrid__item > a").forEach(link => {
    //     link.addEventListener("click", (e) => {
    //         trackClick("subCategory_click", e.currentTarget);
    //     });
    // });
    addSafeEventListener(".subcategoriesGrid__item > a", "click", (e) => {
        trackClick("subCategory_click", e.currentTarget);
    });

    // GA4 view_search_results vs view_item_list — branch on getPageType().
    // 'search_results' = BC page_type=category on /shop/ or with search_query (search.js does NOT run here).
    // 'search'         = BC native search page; search.js already fires view_search_results with the accurate server-side total.
    const ga4PageType = typeof window.getPageType === 'function' ? window.getPageType() : '';

    if (ga4PageType === 'search_results') {
        scheduleViewSearchResults();
    } else if (ga4PageType !== 'search') {
        const listId = window.location.pathname.replace(/^\/|\/$/g, '').replace(/\//g, '_') || 'listing';
        const bcPath = getPageBreadcrumbCategoryPath();
        const listName = bcPath
            || document.querySelector('h1.page-heading, h1')?.textContent?.trim()
            || listId;
        const ga4Cards = Array.from(document.querySelectorAll('article.card')).map(card => {
            const skuText = card.querySelector('.card-text--sku')?.textContent?.trim() || '';
            return {
                sku: skuText.replace(/^SKU:\s*/i, '').trim(),
                name: card.querySelector('.card-title a, .card-title')?.textContent?.trim() || '',
                price: card.querySelector('span.price-primary')?.textContent
                    ? parseFloat(card.querySelector('span.price-primary').textContent.replace(/[^0-9.-]/g, ''))
                    : 0,
                categoryPath: bcPath,
            };
        });
        if (ga4Cards.length) fireViewItemList(ga4Cards, listId, listName);
    }
}
